import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";

interface Package {
  id: number;
  name: string;
  displayName: string;
  description: string;
  pricePerSeat: number;
  adminSeats: number;
  operatorSeats: number;
  features: string[];
}

function normalizePackage(row: Record<string, unknown>): Package {
  return {
    id: Number(row.id),
    name: String(row.name ?? ""),
    displayName: String(row.displayName ?? row.display_name ?? row.name ?? ""),
    description: String(row.description ?? ""),
    pricePerSeat: Number(row.pricePerSeat ?? row.price_per_seat ?? 0),
    adminSeats: Number(row.adminSeats ?? row.admin_seats ?? 1) || 1,
    operatorSeats: Number(row.operatorSeats ?? row.operator_seats ?? 3) || 3,
    features: Array.isArray(row.features) ? row.features as string[] : [],
  };
}

export default function PackageSelectionPage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    // Check if company registration data exists
    const companyData = sessionStorage.getItem("companyRegistration");
    if (!companyData) {
      navigate("/register/company");
      return;
    }

    // Fetch available packages
    fetchPackages();
  }, [navigate]);

  async function fetchPackages() {
    try {
      const response = await apiFetch("/api/packages");
      if (!response.ok) throw new Error("Failed to load packages");
      const data = await response.json();
      setPackages((data.packages || []).map(normalizePackage));
      setLoading(false);
    } catch (err) {
      setError("Unable to load packages. Please try again.");
      setLoading(false);
    }
  }

  function handleContinue() {
    if (!selectedPackage) {
      setError("Please select a package to continue");
      return;
    }

    const pkg = packages.find(p => p.id === selectedPackage);
    if (!pkg) return;

    // Store package selection
    sessionStorage.setItem("selectedPackage", JSON.stringify(pkg));
    navigate("/register/complete");
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading packages...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-16">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <Link to="/" className="inline-flex items-center gap-2 text-foreground hover:opacity-90 mb-8">
            <img src="/logo.png" alt="CompostDeveloper" className="h-10 w-10 rounded" />
            <span className="text-xl font-bold">
              Compost<span className="text-forest-500">Developer</span>
            </span>
          </Link>
          <h1 className="mt-6 text-3xl font-semibold text-foreground">Choose Your Package</h1>
          <p className="mt-2 text-muted-foreground">
            Select the package that best fits your facility's needs
          </p>
        </div>

        {error && (
          <div className="max-w-2xl mx-auto mb-8 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
          {packages.map((pkg) => {
            const totalSeats = pkg.adminSeats + pkg.operatorSeats;
            const monthlyPrice = totalSeats * pkg.pricePerSeat;
            const isSelected = selectedPackage === pkg.id;

            return (
              <div
                key={pkg.id}
                onClick={() => setSelectedPackage(pkg.id)}
                className={`relative rounded-xl border-2 bg-card p-8 cursor-pointer transition-all hover:shadow-lg ${
                  isSelected
                    ? "border-forest-600 shadow-lg ring-2 ring-forest-600/20"
                    : "border-border hover:border-forest-600/50"
                }`}
              >
                {isSelected && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-forest-600 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-foreground">{pkg.displayName}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{pkg.description}</p>
                </div>

                <div className="mb-6 pb-6 border-b border-border">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-foreground">${monthlyPrice}</span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    ${pkg.pricePerSeat}/seat × {totalSeats} seats
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-forest-600"></div>
                    <span className="text-foreground">
                      <strong>{pkg.adminSeats}</strong> Admin seat{pkg.adminSeats > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-forest-600"></div>
                    <span className="text-foreground">
                      <strong>{pkg.operatorSeats}</strong> Operator seat{pkg.operatorSeats > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                <div className="space-y-2.5">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                    Included Features
                  </p>
                  {pkg.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-forest-600 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground capitalize">
                        {feature.replace(/_/g, " ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-4 max-w-md mx-auto">
          <button
            onClick={() => navigate("/register/company")}
            className="flex-1 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-secondary transition-colors font-medium"
          >
            Back
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedPackage}
            className="flex-1 px-6 py-3 bg-forest-600 hover:bg-forest-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
